import * as React from 'react';
import { Checkbox, Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@material-ui/core'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useState, useEffect } from 'react';
import api from '../../services/api';
import toastError from '../../errors/toastError';

/*
  MODELO DE PARAMETRO ESPERADO E RESPOSTA DO COMPONENTE
  interface IConnectionAndQueueIds {
    connectionId: number | string,
    queueIds: number[] | string[],
    performTickets: queueId[],
  }[];
*/

const ConnectionAndQueueConfiguration = ({ selecteds, onSelected, userId, isCollapsed = true }) => {

    const [loading, setLoading] = useState(false);
    const [selectedConnectionOptions, setSelectedConnectionOptions] = useState([]);
    const [connections, setConnections] = useState([]);
    const [collapsedConnections, setCollapsedConnections] = useState([]);

    // Atualiza o estado interno quando a prop inicial muda
    useEffect(() => {
        if (selecteds && Array.isArray(selecteds) && selecteds.length > 0) setSelectedConnectionOptions(selecteds);
    }, [selecteds]);


    useEffect(() => {

        let isMounted = true;
        const abortController = new AbortController();

        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get("/whatsappListAll", {
                    params: { disabled: "0", userId },
                    signal: abortController.signal,
                });
                if (!isMounted || !data) return;

                // filtrar conexões que não tenham filas
                if (Array.isArray(data) && data.length > 0) {
                    const filteredConnections = data.filter(dt => !isNaN(+dt?.id) && (dt?.Queues && Array.isArray(dt.Queues) && dt.Queues.length > 0));
                    setConnections(filteredConnections);
                    if (isCollapsed == true) setCollapsedConnections(filteredConnections.map(f => f.id));
                }
            } catch (err) {
                console.error({ err });
                toastError(err);
            } finally {
                setLoading(false);
            }
        })();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, []);

    useEffect(() => {
        onSelected(selectedConnectionOptions);
    }, [selectedConnectionOptions]);

    // Selecionar ou não conexão como collapsado
    const handleCollapsed = (connId) => {
        setCollapsedConnections((prev) => {
            const alreadyExist = prev.includes(connId);
            if (alreadyExist) return [...prev].filter(cId => cId != connId);
            else return [...prev, connId];
        })
    }

    // Verificar se todas as conexões estão completas
    const isAllSelected = () => {
        let allSelected = true;
        let oneQueueSelected = false;

        connections.map((conn) => {
            // se alguma conexão não estiver com a qtd de filas selecionadas o allSelected é falso
            const selectedOptionsLength = selectedConnectionOptions.find(c => c.connectionId == conn.id)?.queueIds?.length;
            if (!selectedOptionsLength || selectedConnectionOptions < conn.Queues.length) allSelected = false;
            else if (selectedOptionsLength > 0) oneQueueSelected = true;
        });

        return { allSelected, oneQueueSelected };
    }

    const allSelectedProps = isAllSelected();

    const handleSelectAllToggle = () => {
        if (allSelectedProps?.allSelected == true) {
            setSelectedConnectionOptions(connections.map((conn) => {
                return { connectionId: conn.id, queueIds: [], performTickets: [] };
            }));
        }
        else {
            setSelectedConnectionOptions(connections.map((conn) => {
                const qIds = conn.Queues.map((queue => queue.id) || []);
                return { connectionId: conn.id, queueIds: qIds, performTickets: qIds };
            }));
        }
    }

    // Selecionar ou deselecionar uma conexão
    const handleConnectionSelection = (connId) => {
        if (!connId) return;

        // resgatar informações originais da conexão
        const conn = connections.find(c => c.id == connId);
        if (!conn) return;

        // verificar se a conexão já está com todas as opções selecionadas
        let allSelected = true;
        const connSelectedIdx = selectedConnectionOptions.findIndex(sc => sc.connectionId == connId);
        if (connSelectedIdx != -1) {
            const connSelected = selectedConnectionOptions[connSelectedIdx];
            for (let x = 0; x < conn.Queues.length; x++) {
                if (connSelected.queueIds.findIndex((q) => q == conn.Queues[x].id) == -1) {
                    allSelected = false;
                    break;
                }
            }
        } else {
            allSelected = false;
        }

        // se estiver todas as filas selecionadas, deselecionar, e vice-verso
        const response = { connectionId: connId, queueIds: allSelected == true ? [] : conn.Queues.map(queue => queue.id), performTickets: allSelected == true ? [] : conn.Queues.map(queue => queue.id) };

        setSelectedConnectionOptions((prev) => {
            if (connSelectedIdx != -1) {
                const updated = [...prev];
                updated[connSelectedIdx] = response;
                return updated;
            } else {
                return [...prev, response];
            }
        });
    }

    // Selecionar ou deselecionar uma fila
    const handleQueueSelection = (connectionId, queueId) => {
        if (!connectionId || !queueId) return;

        setSelectedConnectionOptions((prev) => {
            const index = prev.findIndex(option => option.connectionId === connectionId);

            // Se a conexão já está na lista
            if (index !== -1) {
                const current = prev[index];
                const queueAlreadySelected = current.queueIds.includes(queueId);

                const updatedQueueIds = queueAlreadySelected ? current.queueIds.filter(id => id !== queueId) : [...current.queueIds, queueId];
                const updatedPerformTickets = queueAlreadySelected ? current.performTickets.filter(id => id !== queueId) : [...current.performTickets, queueId];

                const updated = [...prev];
                updated[index] = { connectionId, queueIds: updatedQueueIds, performTickets: updatedPerformTickets };
                return updated;
            }

            // Se a conexão ainda não está na lista, adiciona com a fila atual
            return [...prev, { connectionId, queueIds: [queueId], performTickets: [queueId] }];
        });
    };

    // Selecionar ou deselecionar um realizar atendimento
    const handlePerformTicketsSelection = (connectionId, queueId) => {
        if (!connectionId || !queueId) return;

        setSelectedConnectionOptions((prev) => {
            const index = prev.findIndex(option => option.connectionId === connectionId);

            // Se a conexão já está na lista
            if (index !== -1) {
                const current = prev[index];
                const performTicketsAlreadySelected = current.performTickets.includes(queueId);

                const updatedQueueIds = performTicketsAlreadySelected
                    ? current.performTickets.filter(id => id !== queueId) // Remove
                    : [...current.performTickets, queueId]; // Adiciona

                const updated = [...prev];
                updated[index] = { connectionId, queueIds: current.queueIds, performTickets: updatedQueueIds };
                return updated;
            }

            // Se a conexão ainda não está na lista, adiciona com a fila atual
            return [...prev, { connectionId, queueIds: [queueId], performTickets: [queueId] }];
        });
    };

    const queueIsSelected = (connId, queueId) => {
        const selectedConnection = selectedConnectionOptions.find((c) => c.connectionId == connId);
        if (!selectedConnection || !selectedConnection?.queueIds || !Array.isArray(selectedConnection.queueIds) || selectedConnection.queueIds.length == 0) return false;
        return selectedConnection.queueIds.findIndex((q) => q == queueId) >= 0 ? true : false;
    }

    const isAllQueueSelected = (connId) => {
        const selectedConnectionQueuesLength = selectedConnectionOptions.find(c => c.connectionId == connId)?.queueIds?.length;
        if (!selectedConnectionQueuesLength || selectedConnectionQueuesLength == 0) return true;
        const connectionQueuesLength = connections.find(c => c.id == connId)?.Queues?.length;

        return (selectedConnectionQueuesLength != connectionQueuesLength) ? false : true;
    }

    const performTicketsIsSelected = (connId, queueId) => {
        const selectedConnection = selectedConnectionOptions.find((c) => c.connectionId == connId);
        if (!selectedConnection || !selectedConnection?.performTickets || !Array.isArray(selectedConnection.performTickets) || selectedConnection.performTickets.length == 0) return false;
        return selectedConnection.performTickets.findIndex((q) => q == queueId) >= 0 ? true : false;
    }

    return (
        <TableContainer component={Paper}>
            <Table aria-label="collapsible table">
                <TableHead>
                    <TableRow>
                        <TableCell
                            align="center"
                        >
                            <Checkbox
                                size="small"
                                color="primary"
                                id={`conn-selectAll`}
                                key={`conn-selectAll`}
                                checked={allSelectedProps?.allSelected === true}
                                indeterminate={allSelectedProps?.allSelected === false && allSelectedProps?.oneQueueSelected === true}
                                onChange={handleSelectAllToggle}
                            />
                        </TableCell>
                        <TableCell
                            colSpan={2}
                            align="left"
                        >
                            Conexão
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        (Array.isArray(connections) && connections.length > 0) &&
                        connections.sort((a, b) => a.name.localeCompare(b.name)).map((conn) => {
                            return (
                                <React.Fragment>
                                    <TableRow
                                        sx={{ '& > *': { borderBottom: 'unset' } }}
                                    >
                                        <TableCell
                                            align="center"
                                        >
                                            <Checkbox
                                                size="small"
                                                color="primary"
                                                id={`conn-${conn.id}`}
                                                key={`conn-${conn.id}`}
                                                checked={selectedConnectionOptions.find(c => c.connectionId == conn.id)?.queueIds?.length > 0 ? true : false}
                                                onChange={() => handleConnectionSelection(conn.id)}
                                                indeterminate={isAllQueueSelected(conn.id) == true ? false : true}
                                            />
                                        </TableCell>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            align="left"
                                        >
                                            {conn.name}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                aria-label="expand row"
                                                size="small"
                                                onClick={() => handleCollapsed(conn.id)}
                                            >
                                                {collapsedConnections.includes(conn.id) == false ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            style={{ paddingBottom: 0, paddingTop: 0 }}
                                            colSpan={6}
                                        >
                                            <Collapse
                                                in={collapsedConnections.includes(conn.id) == false}
                                                timeout="auto"
                                                unmountOnExit
                                            >
                                                <Table size="small" aria-label="purchases">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Fila</TableCell>
                                                            <TableCell>Vincular a Fila</TableCell>
                                                            <TableCell>Realizar Atendimentos?</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {
                                                            Array.isArray(conn?.Queues) && conn.Queues.length > 0 &&
                                                            conn.Queues.map((queue) => {
                                                                return (
                                                                    <TableRow key={`${conn.id}-${queue.id}`}>
                                                                        <TableCell>
                                                                            {queue.name}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Checkbox
                                                                                id={`conn-${conn.id}-${queue.id}`}
                                                                                key={`conn-${conn.id}-${queue.id}`}
                                                                                size="small"
                                                                                color="primary"
                                                                                checked={queueIsSelected(conn.id, queue.id)}
                                                                                onChange={() => handleQueueSelection(conn.id, queue.id)}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Checkbox
                                                                                id={`conn-${conn.id}-${queue.id}-performTickets`}
                                                                                key={`conn-${conn.id}-${queue.id}-performTickets`}
                                                                                size="small"
                                                                                color="primary"
                                                                                disabled={queueIsSelected(conn.id, queue.id) == false}
                                                                                checked={performTicketsIsSelected(conn.id, queue.id)}
                                                                                onChange={() => handlePerformTicketsSelection(conn.id, queue.id)}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )
                                                            })
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })
                    }
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default ConnectionAndQueueConfiguration;